/**
 * Safely get environment variables
 */
function getEnv() {
  try {
    if (typeof import.meta !== 'undefined' && 
        import.meta && 
        typeof import.meta.env === 'object' && 
        import.meta.env !== null) {
      return import.meta.env;
    }
  } catch (error) {
    console.warn('Could not access import.meta.env:', error);
  }
  return {};
}

/**
 * Safely get window location
 */
function getLocation() {
  try {
    if (typeof window !== 'undefined' && 
        window && 
        typeof window.location === 'object' && 
        window.location !== null) {
      return window.location;
    }
  } catch (error) {
    console.warn('Could not access window.location:', error);
  }
  return {};
}

/**
 * Utility to detect if we're running in development mode
 * Uses multiple checks for better reliability across different environments
 */
export function isDevelopmentMode(): boolean {
  try {
    // Check Vite environment variables
    const env = getEnv();
    if (env && (
        env.MODE === 'development' || 
        env.VITE_ENV === 'development' ||
        env.DEV === true ||
        env.NODE_ENV === 'development'
      )) {
      return true;
    }
    
    // Check hostname and port for local development
    const location = getLocation();
    if (location && location.hostname && location.port) {
      const hostname = location.hostname;
      const port = location.port;
      
      return hostname === 'localhost' ||
             hostname === '127.0.0.1' ||
             hostname === '0.0.0.0' ||
             port === '5173' ||  // Vite default
             port === '3000' ||  // Common React dev port
             port === '8080';    // Another common dev port
    }
    
    // Fallback check for localhost without port
    if (location && location.hostname) {
      return location.hostname === 'localhost' ||
             location.hostname === '127.0.0.1';
    }
  } catch (error) {
    console.warn('Error in isDevelopmentMode:', error);
  }
  
  return false;
}

/**
 * Get environment information for debugging
 */
export function getEnvironmentInfo() {
  try {
    const env = getEnv();
    const location = getLocation();
    
    return {
      isDevelopment: isDevelopmentMode(),
      mode: (env && env.MODE) ? env.MODE : 'unknown',
      hostname: (location && location.hostname) ? location.hostname : 'unknown',
      port: (location && location.port) ? location.port : 'unknown',
      protocol: (location && location.protocol) ? location.protocol : 'unknown',
      env: {
        MODE: env && env.MODE,
        DEV: env && env.DEV,
        PROD: env && env.PROD,
        VITE_ENV: env && env.VITE_ENV,
        NODE_ENV: env && env.NODE_ENV
      }
    };
  } catch (error) {
    console.warn('Error in getEnvironmentInfo:', error);
    return {
      isDevelopment: true, // Default to development if we can't determine
      mode: 'unknown',
      hostname: 'unknown',
      port: 'unknown',
      protocol: 'unknown',
      env: {}
    };
  }
}